import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Keyboard } from "@phosphor-icons/react";

/**
 * Global keyboard-shortcut palette. Mounted once at the app root; opens on
 * ``?`` (Shift+/) or ``H`` — closes on Escape. Also wires the go-to-page
 * shortcuts (G+D, G+B, G+T, G+P, G+W) via a small two-key state machine so
 * users don't need modifiers to jump between pages.
 *
 * The single ``?`` key is deliberately overloaded (Shift+/) — this matches
 * how GitHub, Linear, and every mature keyboard-driven web app surface
 * their own palettes, so muscle memory carries over.
 */
const SHORTCUTS = [
  { section: "Navigation", items: [
    { keys: ["G", "D"], desc: "Go to Dashboard" },
    { keys: ["G", "T"], desc: "Go to Trade" },
    { keys: ["G", "P"], desc: "Go to Positions" },
    { keys: ["G", "B"], desc: "Go to Auto Bots" },
    { keys: ["G", "W"], desc: "Go to Watchlist" },
    { keys: ["G", "L"], desc: "Go to Leaderboard" },
  ]},
  { section: "Actions", items: [
    { keys: ["/"], desc: "Open command palette · search symbols & actions" },
    { keys: ["⌘", "K"], desc: "Also opens the command palette (Cmd/Ctrl+K)" },
    { keys: ["P"], desc: "Pin current symbol to watchlist (on /trade)" },
    { keys: ["?"], desc: "Open this shortcut palette" },
    { keys: ["Esc"], desc: "Close any open modal or palette" },
  ]},
];

const NAV_MAP = {
  d: "/",
  t: "/trade",
  p: "/positions",
  b: "/bots",
  w: "/watchlist",
  l: "/leaderboard",
};

export default function ShortcutPalette() {
  const [open, setOpen] = useState(false);
  const [gPending, setGPending] = useState(false); // true once user presses G, waiting for follow-up
  const navigate = useNavigate();

  useEffect(() => {
    const isTyping = (t) => {
      if (!t) return false;
      const tag = (t.tagName || "").toLowerCase();
      return tag === "input" || tag === "textarea" || t.isContentEditable;
    };

    let gTimer = null;
    const handler = (e) => {
      // Never hijack keys while the user is typing.
      if (isTyping(e.target)) return;

      // Escape always closes the palette when open (Dialog also handles it
      // but we mirror here so the G-prefix state resets).
      if (e.key === "Escape") {
        setGPending(false);
        return;
      }

      // Open on "?" (Shift+/) — no other modifiers so browsers' Find isn't hit.
      if (e.key === "?" && !e.ctrlKey && !e.metaKey && !e.altKey) {
        e.preventDefault();
        setOpen((v) => !v);
        setGPending(false);
        return;
      }

      const k = e.key?.toLowerCase();
      if (e.metaKey || e.ctrlKey || e.altKey) return;

      // Two-key G+X navigation. Priming with G opens a 1.5s window for
      // the follow-up key to land — long enough for humans, short enough
      // that a lone G doesn't lock other shortcuts.
      if (gPending) {
        if (NAV_MAP[k]) {
          e.preventDefault();
          navigate(NAV_MAP[k]);
        }
        setGPending(false);
        if (gTimer) clearTimeout(gTimer);
        return;
      }
      if (k === "g") {
        e.preventDefault();
        setGPending(true);
        if (gTimer) clearTimeout(gTimer);
        gTimer = setTimeout(() => setGPending(false), 1500);
      }
    };
    window.addEventListener("keydown", handler);
    return () => {
      window.removeEventListener("keydown", handler);
      if (gTimer) clearTimeout(gTimer);
    };
  }, [gPending, navigate]);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent
        data-testid="shortcut-palette"
        className="bg-zinc-900 border-zinc-800 rounded-sm max-w-md"
      >
        <DialogHeader>
          <DialogTitle className="heading text-2xl tracking-tighter flex items-center gap-2">
            <Keyboard size={22} className="text-[#00C805]" /> Keyboard shortcuts
          </DialogTitle>
          <DialogDescription className="text-zinc-500 text-xs">
            Press <Kbd>?</Kbd> anywhere to toggle this palette · <Kbd>Esc</Kbd> to close.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 mt-2">
          {SHORTCUTS.map((s) => (
            <div key={s.section} data-testid={`shortcut-section-${s.section.toLowerCase()}`}>
              <div className="mono text-[10px] uppercase tracking-widest text-zinc-500 mb-2">
                {s.section}
              </div>
              <ul className="space-y-1.5">
                {s.items.map((it, i) => (
                  <li
                    key={i}
                    data-testid={`shortcut-item-${s.section.toLowerCase()}-${i}`}
                    className="flex items-center justify-between border-b border-zinc-800/50 pb-1.5 last:border-0"
                  >
                    <span className="text-xs text-zinc-300">{it.desc}</span>
                    <span className="flex items-center gap-1">
                      {it.keys.map((k, ki) => (
                        <span key={ki} className="flex items-center gap-1">
                          <Kbd>{k}</Kbd>
                          {ki < it.keys.length - 1 && <span className="mono text-[9px] text-zinc-600">then</span>}
                        </span>
                      ))}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Kbd({ children }) {
  return (
    <kbd className="mono text-[10px] uppercase tracking-widest px-1.5 py-0.5 rounded-sm border border-zinc-700 bg-zinc-950 text-white select-none">
      {children}
    </kbd>
  );
}
